import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notificationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Bell, CheckCheck, FileText, UserPlus, Megaphone } from 'lucide-react';

const iconMap = {
  asignacion: UserPlus,
  estado: FileText,
  dictamen: FileText,
  publicacion: Megaphone,
};

export default function NotificationsPage() {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = () => {
    notificationsAPI.list().then(res => setNotifs(res.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { if (user) load(); }, [user]);

  const markAll = async () => {
    await notificationsAPI.markAllRead();
    load();
  };

  const handleClick = (notif) => {
    if (!notif.leida) {
      notificationsAPI.markRead(notif.id).then(() => load()).catch(() => {});
    }
    if (notif.link) {
      navigate(notif.link);
    }
  };

  return (
    <div className="flex-1 p-8" data-testid="notifications-page">
      <div className="max-w-2xl">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-serif font-bold text-slate-dark">Notificaciones</h1>
          {notifs.some(n => !n.leida) && (
            <Button variant="outline" size="sm" onClick={markAll} className="font-sans text-xs" data-testid="mark-all-read-btn">
              <CheckCheck className="w-3 h-3 mr-1" /> Marcar todas como leidas
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin w-6 h-6 border-2 border-trans-blue border-t-transparent rounded-full" /></div>
        ) : notifs.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-sm border border-gray-100">
            <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" strokeWidth={1} />
            <p className="font-sans text-gray-400">No tienes notificaciones.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifs.map((n, i) => {
              const Icon = iconMap[n.tipo] || Bell;
              return (
                <div
                  key={n.id}
                  className={`p-4 rounded-sm border transition-colors cursor-pointer group ${n.leida ? 'bg-white border-gray-100 hover:bg-gray-50' : 'bg-trans-blue/5 border-trans-blue/20 hover:bg-trans-blue/10'}`}
                  onClick={() => handleClick(n)}
                  data-testid={`notification-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${n.leida ? 'bg-gray-100' : 'bg-trans-blue/10'}`}>
                      <Icon className={`w-4 h-4 ${n.leida ? 'text-gray-400' : 'text-trans-blue'}`} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-sans ${n.leida ? 'text-gray-500' : 'text-slate-dark font-medium'}`}>{n.mensaje}</p>
                      <p className="text-xs font-sans text-gray-400 mt-1">{new Date(n.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                      {n.link && (
                        <p className="text-xs font-sans text-trans-blue mt-1 opacity-0 group-hover:opacity-100 transition-opacity">Ver detalles &rarr;</p>
                      )}
                    </div>
                    {!n.leida && <div className="w-2 h-2 rounded-full bg-trans-blue shrink-0 mt-2" />}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
